import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { openFileInNewTab } from 'src/app/shared/utils/file-link.util';
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { EMPTY, forkJoin, Observable, Subject } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { Sample } from 'src/app/models/sample.model';
import { GridModel } from 'src/app/shared/grid/grid.model';
import { LabRequisition } from 'src/app/models/labRequisition.model';
import { FileAttachment } from 'src/app/models/fileAttachment.model';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { SampleService } from 'src/app/services/sample.service';
import { LabRequisitionService } from 'src/app/services/lab-requisition.service';
import { ToastService } from 'src/app/services/toast.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { CommonService } from 'src/app/services/common.service';
import { AuthService } from 'src/app/services';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { SampleStatusEnum, SampleStatusColors } from 'src/app/enums/sampleStatus.enum';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { Units } from 'src/app/enums/units.enum';
import { TWACorrectionMethod } from 'src/app/enums/TWACorrectionMethod.enum';
import { UnlinkCocDialogComponent } from 'src/app/modals/unlink-coc-dialog/unlink-coc-dialog.component';
import { LabResultImportDialogComponent } from 'src/app/modals/lab-result-import-dialog/lab-result-import-dialog.component';
import { LabResultImportDialogData } from 'src/app/modals/lab-result-import-dialog/lab-result-import.model';
import { LabResultImportService, labResultTemplateFileName } from 'src/app/modals/lab-result-import-dialog/lab-result-import.service';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { IVoidReasonResult, VoidReasonDialogComponent } from 'src/app/modals/void-reason-dialog/void-reason-dialog.component';
import nameOf from 'src/app/shared/form/nameOf.helper';

interface SampleStatusPatchRequest {
    sampleStatus: SampleStatusEnum;
    voidReason?: string;
}

interface SampleRowData {
    sample: Sample;
    agent: IhLabResult | null;
    isFirstRow: boolean;
    rowspan: number;
    canUnlink: boolean;
    statusLabel: string;
    categoryLabel: string;
}

interface DetailField {
    id: string;
    label: string;
    value: string;
}

interface NestedColumn {
    key: string;
    label: string;
    getValue: (agent: IhLabResult) => string;
}

@Component({
  standalone: false,
    selector: 'app-lab-requisition-details-popup',
    templateUrl: './lab-requisition-details-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './lab-requisition-details-popup.component.scss',
})
export class LabRequisitionDetailsPopupComponent implements OnInit, OnDestroy {
    labRequisitionDetails: LabRequisition;
    massColKey = nameOf<IhLabResult>(x => x.totalMass);
    displayedColumns: string[] = ['sampleId', 'sampleCategory', 'agent', this.massColKey, 'concentration', 'unsampledTimeCorrection',
        'unsampledTimeConcentration', 'twaCorrectionMethod', 'twa', 'status', 'actions'];
    showBlankCorrectedMass = false;
    dataSource = new MatTableDataSource<SampleRowData>();
    isLoading = false;
    totalSamples = 0;
    pageIndex = 0;
    pageSize = 10;
    readonly pageSizeOptions = [5, 10, 20, 50];
    sortActive = '';
    sortDirection: SortDirection = '';
    sortIcons: Record<string, string> = {};
    sampleStatusEnum = SampleStatusEnum;
    sampleStatusColors = SampleStatusColors;
    sampleStatusOptions: { value: number; label: string }[] = [];
    detailFields: DetailField[] = [];
    nestedColumns: NestedColumn[] = [];
    private destroy$ = new Subject<void>();
    private readonly reloadSamples$ = new Subject<void>();
    private readonly defaultOrderBy = nameOf<Sample>(x => x.id);
    private readonly sortFields: Record<string, string> = {
        sampleId: nameOf<Sample>(x => x.sampleNumber),
        sampleCategory: nameOf<Sample>(x => x.sampleCategory),
        agent: `${nameOf<Sample>(x => x.ihLabResults)}.${nameOf<IhLabResult>(x => x.agentId)}`,
        status: nameOf<Sample>(x => x.sampleStatus),
    };

    constructor(
        @Inject(FLYOUT_DATA) public labRequisitionId: string,
        private labRequisitionService: LabRequisitionService,
        private sampleService: SampleService,
        private toastService: ToastService,
        private dateFormatService: DateFormatService,
        private valueConversionService: ValueConversionService,
        private commonService: CommonService,
        private authService: AuthService,
        private labResultImportService: LabResultImportService,
        protected dialog: MatDialog,
    ) { }

    ngOnInit(): void {
        this.initializeStatusOptions();
        this.initializeNestedColumns();
        this.updateSortIcons();
        this.subscribeToSampleReloads();
        this.loadData();
    }

    private subscribeToSampleReloads(): void {
        this.reloadSamples$.pipe(
            switchMap(() => this.getSamples().pipe(
                catchError((error) => {
                    this.toastService.showError(error);
                    return EMPTY;
                })
            )),
            takeUntil(this.destroy$)
        ).subscribe((response) => {
            this.totalSamples = response.count;
            this.processSamples(response.data);
        });
    }

    private getSamples(): Observable<GridModel<Sample>> {
        const filterBy = JSON.stringify({ labRequisitionId: this.labRequisitionId });
        const sortField = this.sortDirection ? this.sortFields[this.sortActive] : undefined;
        return this.sampleService.get('', sortField ?? this.defaultOrderBy, sortField ? this.sortDirection : 'asc',
            this.pageIndex, this.pageSize, filterBy);
    }

    onSortChange(sort: Sort): void {
        this.sortActive = sort.active;
        this.sortDirection = sort.direction;
        this.pageIndex = 0;
        this.updateSortIcons();
        this.loadPagedData();
    }

    private updateSortIcons(): void {
        this.sortIcons = Object.fromEntries(Object.keys(this.sortFields).map(column => [
            column,
            column === this.sortActive && this.sortDirection
                ? (this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward')
                : 'unfold_more'
        ]));
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.loadPagedData();
    }

    private initializeStatusOptions(): void {
        this.sampleStatusOptions = Object.keys(SampleStatusEnum)
            .filter(key => !isNaN(Number(SampleStatusEnum[key])))
            .map(key => ({
                value: SampleStatusEnum[key],
                label: this.commonService.getEnumName(SampleStatusEnum, SampleStatusEnum[key])
            }));
    }

    private initializeNestedColumns(): void {
        this.nestedColumns = [
            { key: this.massColKey, label: 'Mass', getValue: (agent) => this.getMass(agent) },
            { key: 'concentration', label: 'Concentration', getValue: (agent) => this.getConcentration(agent) },
            { key: 'unsampledTimeCorrection', label: 'Unsampled Time Correction', getValue: (agent) => this.getUnsampledTimeCorrection(agent) },
            { key: 'unsampledTimeConcentration', label: 'Unsampled Time Concentration', getValue: (agent) => this.getUnsampledTimeConcentration(agent) },
            { key: 'twaCorrectionMethod', label: 'TWA Correction Method', getValue: (agent) => this.getTwaCorrectionMethod(agent) },
            { key: 'twa', label: 'TWA', getValue: (agent) => this.getTwa(agent) }
        ];
    }

    private updateDetailFields(): void {
        this.detailFields = [
            { id: 'labRequisitionNumber', label: 'Lab Requisition Number', value: this.labRequisitionDetails?.labRequisitionNumber || '-' },
            { id: 'received', label: 'Received', value: '' },
            { id: 'due', label: 'Due', value: this.formatDate(this.labRequisitionDetails?.emailEdd) },
            { id: 'survey', label: 'Survey', value: '-' },
            { id: 'site', label: 'Site', value: '-' },
        ];
    }

    loadData(): void {
        this.isLoading = true;
        this.pageIndex = 0;
        this.pageSize = 10;

        forkJoin({
            labRequisitionDetails: this.labRequisitionService.getById(this.labRequisitionId),
            samples: this.getSamples()
        }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (response) => {
                this.labRequisitionDetails = response.labRequisitionDetails;
                this.updateDetailFields();
                this.totalSamples = response.samples.count;
                this.processSamples(response.samples.data);
                this.isLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            }
        });
    }

    loadPagedData(): void {
        this.reloadSamples$.next();
    }

    processSamples(samples: Sample[]): void {
        const flattenedData: SampleRowData[] = [];

        for (const sample of samples) {
            const agents = sample.ihLabResults;
            const lockedStatuses = [
                SampleStatusEnum.Approved,
                SampleStatusEnum.ApprovedLetterSent,
                SampleStatusEnum.Void,
                SampleStatusEnum.VoidLetterSent
            ];
            const currentUser = this.authService.getCurrentUser();
            const canUnlink = !lockedStatuses.includes(sample.sampleStatus) &&
                !!currentUser &&
                [SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType);
            const statusLabel = this.getStatusLabel(sample.sampleStatus);
            const categoryLabel = SampleCategoryEnum[sample.sampleCategory] ?? '-';

            if (!agents?.length) {
                flattenedData.push({
                    sample,
                    agent: null,
                    isFirstRow: true,
                    rowspan: 1,
                    canUnlink,
                    statusLabel,
                    categoryLabel
                });
            } else {
                const rowspan = agents.length;
                for (let i = 0; i < rowspan; i++) {
                    flattenedData.push({
                        sample,
                        agent: agents[i],
                        isFirstRow: i === 0,
                        rowspan,
                        canUnlink,
                        statusLabel,
                        categoryLabel
                    });
                }
            }
        }

        this.dataSource.data = flattenedData;
        this.updateBlankCorrectedMassVisibility(flattenedData);
    }

    private updateBlankCorrectedMassVisibility(data: SampleRowData[]): void {
        if (this.showBlankCorrectedMass) return;
        const hasBlankCorrection = data.some(row => row.agent?.blankCorrectionApplied);
        if (hasBlankCorrection) {
            this.showBlankCorrectedMass = true;
            const blankColKey = nameOf<IhLabResult>(x => x.blankCorrectedMass);
            const massIndex = this.displayedColumns.indexOf(this.massColKey);
            this.displayedColumns.splice(massIndex + 1, 0, blankColKey);
            this.nestedColumns.splice(1, 0, {
                key: blankColKey,
                label: 'Blank Corrected Mass',
                getValue: (agent) => this.getBlankCorrectedMass(agent)
            });
        }
    }

    getBlankCorrectedMass(agent: IhLabResult): string {
        if (!agent?.blankCorrectionApplied || agent?.blankCorrectedMass == null) return '-';
        const massValue = agent.agentUnits === Units.MgPerM3
            ? this.valueConversionService.convertUgPerM3ToMgPerM3(agent.blankCorrectedMass)
            : agent.blankCorrectedMass;
        const massUnit = agent.agentUnits === Units.MgPerM3 ? 'mg' : 'µg';
        return `${massValue.toFixed(4)} ${massUnit}`;
    }

    formatDate(date: Date | string | number): string {
        if (!date) return '-';
        try {
            return this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
                date.toString(),
                this.dateFormatService.userDateFormat
            );
        } catch {
            return '-';
        }
    }

    getStatusLabel(status: SampleStatusEnum): string {
        return this.commonService.getEnumName(SampleStatusEnum, status) || '-';
    }

    onStatusChange(sample: Sample, newStatus: SampleStatusEnum): void {
        if (newStatus === SampleStatusEnum.Void) {
            const dialogRef = this.dialog.open(VoidReasonDialogComponent, {
                data: { voidReason: sample.voidReason || '' },
                width: '400px',
            });
            dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: string | IVoidReasonResult) => {
                const voidReason = typeof result === 'string'
                    ? result
                    : result?.confirmed
                        ? (result.voidReason || '')
                        : '';
                if (voidReason) {
                    this.performStatusUpdate(sample, newStatus, voidReason);
                }
            });
        } else {
            this.performStatusUpdate(sample, newStatus);
        }
    }

    private performStatusUpdate(sample: Sample, newStatus: SampleStatusEnum, voidReason?: string): void {
        const updatePayload: SampleStatusPatchRequest = { sampleStatus: newStatus };
        if (voidReason) {
            updatePayload.voidReason = voidReason;
        }
        this.sampleService.patch(sample.id, updatePayload).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                sample.sampleStatus = newStatus;
                if (voidReason) {
                    sample.voidReason = voidReason;
                }
                this.toastService.showSuccess('Sample status updated successfully', true);
            },
            error: (error) => {
                this.toastService.showError(error);
            }
        });
    }

    private formatValueWithUnit(value: number | undefined, agent: IhLabResult): string {
        if (!value) return '-';
        const convertedValue = this.valueConversionService.getParameterValueAsPerAgentUnit(value, agent.agentUnits);
        const unit = this.valueConversionService.getParameterUnitSymbol(agent.agentUnits);
        return unit ? `${convertedValue.toFixed(4)} ${unit}` : convertedValue.toFixed(4);
    }

    getMass(agent: IhLabResult): string {
        if (!agent?.totalMass) return '-';
        const massValue = agent.agentUnits === Units.MgPerM3
            ? this.valueConversionService.convertUgPerM3ToMgPerM3(agent.totalMass)
            : agent.totalMass;
        const massUnit = agent.agentUnits === Units.MgPerM3 ? 'mg' : 'µg';
        return `${massValue.toFixed(4)} ${massUnit}`;
    }

    getConcentration(agent: IhLabResult): string {
        return this.formatValueWithUnit(agent?.concValue, agent);
    }

    getUnsampledTimeConcentration(agent: IhLabResult): string {
        return this.formatValueWithUnit(agent?.unsampledTimeConcValue, agent);
    }

    getTwa(agent: IhLabResult): string {
        return this.formatValueWithUnit(agent?.twa, agent);
    }

    getTwaCorrectionMethod(agent: IhLabResult): string {
        return agent?.twaCorrectionMethod != null ? TWACorrectionMethod[agent?.twaCorrectionMethod] : '-';
    }

    getUnsampledTimeCorrection(agent: IhLabResult): string {
        return agent?.unsampledTimeConcValue ? 'Yes' : 'No';
    }

    canUnlinkSample(element: SampleRowData): boolean {
        // Check if sample is in a locked (non-editable) status
        const lockedStatuses = [
            SampleStatusEnum.Approved,
            SampleStatusEnum.ApprovedLetterSent,
            SampleStatusEnum.Void,
            SampleStatusEnum.VoidLetterSent
        ];

        if (lockedStatuses.includes(element.sample.sampleStatus)) {
            return false;
        }

        // Check if current user has permission (only Admin and SuperAdmin can unlink)
        const currentUser = this.authService.getCurrentUser();
        if (!currentUser || ![SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType)) {
            return false;
        }

        return true;
    }

    openImportResults(): void {
        // Only Admin/SuperAdmin may import results — mirrors the commit authorization at the BE and the
        // guard on other sensitive actions in this component (canUnlinkSample). Blocks exposing the full
        // mapping/analysis UI to unauthorized users before any authorization is asserted.
        const currentUser = this.authService.getCurrentUser();
        if (!currentUser || ![SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType)) {
            this.toastService.displayErrorMessage('You do not have permission to import lab results', true);
            return;
        }
        const data: LabResultImportDialogData = {
            labRequisitionId: this.labRequisitionId,
            labRequisitionNumber: this.labRequisitionDetails?.labRequisitionNumber
        };
        const dialogRef = this.dialog.open(LabResultImportDialogComponent, {
            data,
            width: '900px',
            maxHeight: '90vh',
            disableClose: true
        });
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((imported: boolean) => {
            if (imported) {
                this.loadData();
            }
        });
    }

    // Admin/SuperAdmin only — the template endpoint is Admin-gated on the BE, so the button is hidden for everyone
    // else (mirrors openImportResults / canUnlinkSample) rather than exposing an always-403 control.
    get canDownloadResultTemplate(): boolean {
        const currentUser = this.authService.getCurrentUser();
        return !!currentUser && [SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType);
    }

    // DASH-2722: regenerate and download the empty lab-result template on demand, at any requisition status.
    // Nothing is persisted — each request rebuilds the CSV from current requisition data.
    downloadResultTemplate(): void {
        const fileName = labResultTemplateFileName(this.labRequisitionDetails?.labRequisitionNumber || String(this.labRequisitionId));
        this.labResultImportService.downloadTemplate(this.labRequisitionId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (blob) => downloadBlob(blob, fileName),
            // Localized message (es-CL included); the raw BE error is a Blob showError can't render meaningfully.
            error: () => this.toastService.displayErrorMessage('Lab result template could not be generated', true)
        });
    }

    openAttachment(attachment: FileAttachment): void {
        if (!openFileInNewTab(attachment.url)) {
            this.toastService.displayErrorMessage('File URL not available. Please contact support', true);
        }
    }

    onUnlinkSample(element: SampleRowData): void {
        const dialogRef = this.dialog.open(UnlinkCocDialogComponent, {
            data: {
                sampleNumber: element.sample.sampleNumber,
                labReqNumber: this.labRequisitionDetails?.labRequisitionNumber,
                sampleId: element.sample.id
            },
            width: '500px',
            disableClose: true
        });

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result === true) {
                if (this.dataSource.data.filter(r => r.sample.id !== element.sample.id).length === 0 && this.pageIndex > 0) {
                    this.pageIndex--;
                }
                this.loadPagedData();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public trackByIndex = trackByIndex;
}